<template>
  <section class="relative h-[70vh] md:h-screen">
    <!-- Loading Indicator -->
    <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-gray-100">
      <div class="w-12 h-12 border-4 border-empire-yellow border-t-transparent rounded-full animate-spin"></div>
    </div>

    <!-- Carousel Container -->
    <div v-else class="absolute inset-0 overflow-hidden">
      <!-- Images -->
      <div class="flex h-full transition-transform duration-300 ease-in-out" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
        <div
          v-for="(banner, index) in banners"
          :key="index"
          class="w-full h-full flex-shrink-0 relative"
        >
          <img
            :src="banner.image_url"
            :alt="`Banner ${index + 1}`"
            class="w-full h-full object-cover"
          >

          <!-- Overlay with content -->
          <div class="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div class="text-center px-4 md:px-8">
              <h1
                class="text-xl sm:text-3xl md:text-6xl font-bold mb-1 md:mb-4"
                :style="{ color: banner.title_color }"
              >
                {{ getLocalizedTitle(banner) }}
              </h1>
              <p
                class="text-xs sm:text-base md:text-xl mb-3 md:mb-6 max-w-2xl mx-auto"
                :style="{ color: banner.subtitle_color }"
              >
                {{ getLocalizedSubtitle(banner) }}
              </p>
              <component
                :is="isExternalLink(banner.button_link) ? 'a' : 'router-link'"
                :to="isExternalLink(banner.button_link) ? null : (banner.button_link || '/categories')"
                :href="isExternalLink(banner.button_link) ? banner.button_link : null"
                :target="isExternalLink(banner.button_link) ? '_blank' : null"
                :rel="isExternalLink(banner.button_link) ? 'noopener noreferrer' : null"
                class="bg-empire-yellow text-black px-3 sm:px-5 md:px-8 py-1.5 sm:py-2.5 md:py-4 rounded-full text-xs sm:text-sm md:text-lg font-semibold hover:bg-yellow-500 transition-colors inline-block cursor-pointer"
                :style="{ whiteSpace: 'nowrap' }"
              >
                {{ getLocalizedButtonText(banner) || $t('hero.shopNow') }}
              </component>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Arrows -->
      <button
        v-if="banners.length > 1"
        @click="prevSlide"
        class="absolute left-2 md:left-8 top-[60%] md:top-1/2 -translate-y-1/2 w-10 h-10 md:w-[72px] md:h-[72px] flex items-center justify-center md:bg-white/20 md:border-2 md:border-white transition-colors"
      >
        <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="md:w-12 md:h-12">
          <path d="M31.5 12L18 24L31.5 36" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <button
        v-if="banners.length > 1"
        @click="nextSlide"
        class="absolute right-2 md:right-8 top-[60%] md:top-1/2 -translate-y-1/2 w-10 h-10 md:w-[72px] md:h-[72px] flex items-center justify-center md:bg-white/20 md:border-2 md:border-white transition-colors"
      >
        <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="transform rotate-180 md:w-12 md:h-12">
          <path d="M31.5 12L18 24L31.5 36" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Dots Indicator -->
      <div v-if="banners.length > 1" class="absolute bottom-[40px] md:bottom-[72px] left-1/2 -translate-x-1/2 flex flex-row items-center gap-[13px] max-w-[90vw] overflow-x-auto scrollbar-hide">
        <div
          v-for="(_, index) in banners"
          :key="index"
          @click="currentSlide = index"
          class="flex justify-center items-center transition-all duration-300 flex-shrink-0"
          :class="[
            currentSlide === index
              ? 'bg-[#FFDD00]'
              : 'bg-white/15 border-2 border-white',
            // Responsive size based on the number of banners
            banners.length <= 3 ? 'w-[72px] h-[6px]' : // Original size for 3 or fewer
            banners.length <= 5 ? 'w-[50px] md:w-[72px] h-[6px]' : // Medium size for 4-5
            'w-[30px] md:w-[72px] h-[6px]' // Small size for 6+
          ]"
        ></div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

const { locale } = useI18n()

// State
const currentSlide = ref(0)
const banners = ref([])
const loading = ref(true)
const autoSlideInterval = ref(null)

// API URL
const API_URL = process.env.NODE_ENV === 'development' 
  ? 'https://empirecanaca.ca/api' 
  : process.env.VUE_APP_API_URL;

// Helper function to get localized title based on current locale
const getLocalizedTitle = (banner) => {
  const currentLocale = locale.value
  return banner[`title_${currentLocale}`] || banner.title_en || banner.title || ''
}

// Helper function to get localized subtitle based on current locale
const getLocalizedSubtitle = (banner) => {
  const currentLocale = locale.value
  return banner[`subtitle_${currentLocale}`] || banner.subtitle_en || banner.subtitle || ''
}

// Helper function to get localized button text based on current locale
const getLocalizedButtonText = (banner) => {
  const currentLocale = locale.value
  return banner[`button_text_${currentLocale}`] || banner.button_text_en || banner.button_text_pt || banner.button_text_fr || ''
}

// Helper function to check if link is external
const isExternalLink = (link) => {
  if (!link) return false
  return link.startsWith('http://') || link.startsWith('https://')
}

// Fetch banners
const fetchBanners = async () => {
  try {
    const response = await axios.get(`${API_URL}/settings/home-banners?active=true`)
    banners.value = response.data.sort((a, b) => a.position - b.position)

    // Fallback to default banners if none are returned
    if (banners.value.length === 0) {
      banners.value = [
        {
          id: 'default',
          title: 'Your Best Online Shop',
          title_en: 'Your Best Online Shop',
          title_pt: 'Sua Melhor Loja Online',
          title_fr: 'Votre Meilleure Boutique en Ligne',
          subtitle: 'Discover our amazing collection of products with the best prices in the market.',
          subtitle_en: 'Discover our amazing collection of products with the best prices in the market.',
          subtitle_pt: 'Descubra nossa incrível coleção de produtos com os melhores preços do mercado.',
          subtitle_fr: 'Découvrez notre incroyable collection de produits aux meilleurs prix du marché.',
          image_url: '/images/banner/hero-image-1.png',
          title_color: '#FFFFFF',
          subtitle_color: '#FFFFFF'
        }
      ]
    }
  } catch (err) {
    console.error('Error fetching banners:', err)
    // Fallback to default banner
    banners.value = [
      {
        id: 'default',
        title: 'Your Best Online Shop',
        title_en: 'Your Best Online Shop',
        title_pt: 'Sua Melhor Loja Online',
        title_fr: 'Votre Meilleure Boutique en Ligne',
        subtitle: 'Discover our amazing collection of products with the best prices in the market.',
        subtitle_en: 'Discover our amazing collection of products with the best prices in the market.',
        subtitle_pt: 'Descubra nossa incrível coleção de produtos com os melhores preços do mercado.',
        subtitle_fr: 'Découvrez notre incroyable collection de produits aux meilleurs prix du marché.',
        image_url: '/images/banner/hero-image-1.png',
        title_color: '#FFFFFF',
        subtitle_color: '#FFFFFF'
      }
    ]
  } finally {
    loading.value = false
  }
}

// Navigation methods
const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % banners.value.length
}

const prevSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + banners.value.length) % banners.value.length
}

// Auto slide
const startAutoSlide = () => {
  if (banners.value.length > 1) {
    autoSlideInterval.value = setInterval(nextSlide, 9000)
  }
}

// Lifecycle hooks
onMounted(async () => {
  await fetchBanners()
  startAutoSlide()
})

onUnmounted(() => {
  if (autoSlideInterval.value) {
    clearInterval(autoSlideInterval.value)
  }
})
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Hide scrollbar but keep functionality */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}
</style>


